import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

export const get_support_tickets = createAsyncThunk(
    'supportAdmin/list',
    async ({ page = 1, limit = 20, status = '', category = '', priority = '', search = '' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/admin/support/tickets', { params: { page, limit, status, category, priority, search }, withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const get_support_ticket = createAsyncThunk(
    'supportAdmin/detail',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/support/tickets/${id}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const admin_reply_ticket = createAsyncThunk(
    'supportAdmin/reply',
    async ({ id, message }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support/tickets/${id}/messages`, { message }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const update_support_status = createAsyncThunk(
    'supportAdmin/status',
    async ({ id, status }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/support/tickets/${id}/status`, { status }, { withCredentials: true })
            return fulfillWithValue({ ...data, status })
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const add_internal_note = createAsyncThunk(
    'supportAdmin/note',
    async ({ id, note }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support/tickets/${id}/internal-notes`, { note }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const request_seller_response = createAsyncThunk(
    'supportAdmin/requestSeller',
    async ({ id, sellerId, message, relatedOrderId }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support/tickets/${id}/request-seller-response`, { sellerId, message, relatedOrderId }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const create_finance_action = createAsyncThunk(
    'supportAdmin/financeAction',
    async ({ id, ...payload }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support/tickets/${id}/finance-actions`, payload, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const order_refund_chargeback = createAsyncThunk(
    'supportAdmin/orderRefundChargeback',
    async ({ id, ...payload }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/admin/support/tickets/${id}/order-refund-chargeback`, payload, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Xəta' })
        }
    }
)

export const get_ticket_context = createAsyncThunk(
    'supportAdmin/context',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/support/tickets/${id}/context`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) { return rejectWithValue(error.response?.data || { error: 'Xəta' }) }
    }
)

export const search_support_sellers = createAsyncThunk(
    'supportAdmin/sellerSearch',
    async (q, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/admin/support/sellers/search', { params: { q }, withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) { return rejectWithValue(error.response?.data || { error: 'Xəta' }) }
    }
)

const supportTicketReducer = createSlice({
    name: 'supportAdmin',
    initialState: {
        tickets: [], total: 0, totalPages: 1, stats: {},
        ticket: null, context: null, sellerResults: [],
        loader: false, successMessage: '', errorMessage: ''
    },
    reducers: {
        messageClear: (state) => { state.successMessage = ''; state.errorMessage = '' },
        clearTicket: (state) => { state.ticket = null; state.context = null; state.sellerResults = [] },
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_support_tickets.pending, (state) => { state.loader = true })
            .addCase(get_support_tickets.fulfilled, (state, { payload }) => {
                state.loader = false
                state.tickets = payload.tickets || []
                state.total = payload.total || 0
                state.totalPages = payload.totalPages || 1
                state.stats = payload.stats || {}
            })
            .addCase(get_support_tickets.rejected, (state) => { state.loader = false })

            .addCase(get_support_ticket.fulfilled, (state, { payload }) => { state.ticket = payload.ticket || null })

            .addCase(admin_reply_ticket.fulfilled, (state, { payload }) => { state.ticket = payload.ticket || state.ticket; state.successMessage = payload.message })
            .addCase(admin_reply_ticket.rejected, (state, { payload }) => { state.errorMessage = payload?.error || 'Göndərilmədi' })

            .addCase(update_support_status.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                if (state.ticket) state.ticket.status = payload.status
            })
            .addCase(update_support_status.rejected, (state, { payload }) => { state.errorMessage = payload?.error || 'Xəta' })

            .addCase(add_internal_note.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                if (state.ticket) state.ticket.internalNotes = payload.internalNotes
            })
            .addCase(add_internal_note.rejected, (state, { payload }) => { state.errorMessage = payload?.error || 'Xəta' })

            .addCase(request_seller_response.fulfilled, (state, { payload }) => { state.ticket = payload.ticket || state.ticket; state.successMessage = payload.message })
            .addCase(request_seller_response.rejected, (state, { payload }) => { state.errorMessage = payload?.error || 'Xəta' })

            .addCase(create_finance_action.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                if (state.ticket) state.ticket.financeActions = payload.financeActions
            })
            .addCase(create_finance_action.rejected, (state, { payload }) => { state.errorMessage = payload?.error || 'Xəta' })

            .addCase(order_refund_chargeback.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                if (state.ticket && payload.financeActions) state.ticket.financeActions = payload.financeActions
            })
            .addCase(order_refund_chargeback.rejected, (state, { payload }) => { state.errorMessage = payload?.error || payload?.message || 'Pul qaytarma alınmadı' })

            .addCase(get_ticket_context.fulfilled, (state, { payload }) => { state.context = payload })
            .addCase(search_support_sellers.fulfilled, (state, { payload }) => { state.sellerResults = payload.sellers || [] })
    }
})

export const { messageClear, clearTicket } = supportTicketReducer.actions
export default supportTicketReducer.reducer

export const SUPPORT_CATEGORY_LABEL = {
    order_problem: 'Sifariş problemi', product_not_delivered: 'Məhsul təhvil verilmədi',
    product_not_working: 'Məhsul işləmir', payment_balance: 'Ödəniş / balans',
    seller_complaint: 'Satıcıdan şikayət', account_login: 'Hesab / giriş',
    seller_application: 'Satıcı olmaq', other: 'Digər'
}
export const SUPPORT_STATUS = {
    open: { label: 'Açıq', cls: 'bg-blue-500/20 text-blue-300' },
    waiting_support: { label: 'Dəstək gözləyir', cls: 'bg-amber-500/20 text-amber-300' },
    waiting_customer: { label: 'Müştəri gözlənilir', cls: 'bg-sky-500/20 text-sky-300' },
    resolved: { label: 'Həll olundu', cls: 'bg-emerald-500/20 text-emerald-300' },
    closed: { label: 'Bağlandı', cls: 'bg-slate-600/40 text-slate-300' },
}
export const SUPPORT_STATUSES = ['open', 'waiting_support', 'waiting_customer', 'resolved', 'closed']
